import { FormattedHTMLMessage } from "react-intl";
import { produce } from "immer";

import { useFormSelectChoice } from "@/components/FormInputs/InputSelect/InputSelectComponent";

import { VerificationStepsEnum } from "../../lib/types/runtimeTypes";
import { hasFailedInstantMatch } from "../../lib/VerificationService/VerificationServiceHelpers";
import {
  type LicensedProfessionalStatus,
  type CustomFormFieldConfig,
  type FormSelectChoice,
  type LicensedProfessionalPersonalInfoResponse,
  type LicensedProfessionalPersonalInfoViewModel,
  type Organization,
  type VerificationService,
} from "../../lib/types/types";
import {
  getFieldConfigByFieldId,
  handleEmailOnKeyDown,
  updateFieldValidationErrorsByFieldId,
  updateViewModelOrganization,
} from "../../lib/utils/stepComponentHelpers/stepComponentHelpers";
import { useCollectFieldsEndpoint } from "../../lib/ServerApi/useCollectFieldsEndpoint";
import { isCountrySelectionNeeded } from "../FormFields/Country/countryUtils";
import { BirthDateComponent as BirthDate } from "../FormFields/BirthDate/BirthDateComponent";
import { EmailComponent as Email } from "../FormFields/Email/EmailComponent";
import { PhoneNumberComponent as PhoneNumber } from "../FormFields/PhoneNumber/PhoneNumberComponent";
import { FirstNameComponent as FirstName } from "../FormFields/FirstName/FirstNameComponent";
import { LastNameComponent as LastName } from "../FormFields/LastName/LastNameComponent";
import { StatusComponent as LicensedProfessionalStatusComponent } from "../FormFields/Status/StatusComponent";
import { LicensedProfessionalOrganizationComponent as LicensedProfessionalOrganization } from "../FormFields/LicensedProfessionalOrganization/LicensedProfessionalOrganizationComponent";
import { MarketConsentWrapperComponent as MarketConsentWrapper } from "../FormFields/MarketConsentWrapper/MarketConsentWrapperComponent";
import { PostalCodeComponent } from "../FormFields/PostalCode/PostalCodeComponent";
import { getOptions } from "../../options/options";
import { FormFieldCustom } from "../FormFields/FormFieldCustom/FormFieldCustom";
import { ExtraMessage } from "../ExtraMessage";
import { ChangeLocaleV2Component } from "../FormFields/ChangeLocale/ChangeLocaleComponent";
import { CountryComponentWrapper } from "../FormFields/Country/CountryComponentWrapper";
import { PersonalInfoWrapper } from "../PersonalInfoStepWrapper/PersonalInfoStepWrapper";
import { PersonalInfoStepSubmitButton } from "../PersonalInfoStepSubmitButton/PersonalInfoStepSubmitButton";
import { PersonalInfoHeader } from "../FormHeader/StepHeaders/PersonalInfoHeaderComponent";
import { CaptchaComponent } from "../FormFields/Captcha/CaptchaComponent";

interface StepLicensedProfessionalPersonalInfoComponentProps {
  verificationService: VerificationService;
}

export const StepLicensedProfessionalPersonalInfoComponent = ({
  verificationService,
}: StepLicensedProfessionalPersonalInfoComponentProps) => {
  const viewModel = verificationService.viewModel as LicensedProfessionalPersonalInfoViewModel;
  const { fieldValidationErrors } = verificationService;
  const verificationResponse =
    verificationService.verificationResponse as LicensedProfessionalPersonalInfoResponse;
  const failedInstantMatch = hasFailedInstantMatch(verificationResponse);

  const locale = viewModel.localeChoice.value;
  const options = getOptions();

  const { availableStatuses: dynamicStatuses } = useCollectFieldsEndpoint({
    verificationId: verificationResponse.verificationId,
    currentStep: verificationResponse.currentStep,
    viewModel,
  });

  const statusOptions = useFormSelectChoice(
    (dynamicStatuses ?? verificationResponse.availableStatuses) as LicensedProfessionalStatus[],
  );

  const countryChoiceValue = viewModel.countryChoice?.value;
  const needToPickCountry = isCountrySelectionNeeded(
    verificationService.programTheme,
    countryChoiceValue,
  );

  const updateLicensedProfessionalViewModel = (
    key: keyof LicensedProfessionalPersonalInfoViewModel,
    value: any,
  ) => {
    const nextState: LicensedProfessionalPersonalInfoViewModel = produce(
      viewModel,
      (draft: LicensedProfessionalPersonalInfoViewModel) => {
        (draft[key] as any) = value;
      },
    );
    verificationService.updateViewModel(nextState);
  };

  return (
    <PersonalInfoWrapper
      verificationService={verificationService}
      step={verificationResponse.currentStep}
    >
      <PersonalInfoHeader
        verificationService={verificationService}
        classNameIdentifier="licensed-professional"
      />

      <ChangeLocaleV2Component verificationService={verificationService} />
      <CountryComponentWrapper
        verificationService={verificationService}
        disabled={getFieldConfigByFieldId("country")?.disabled}
      />

      <LicensedProfessionalStatusComponent
        segmentId="licensed-professional"
        value={statusOptions.find((option) => option.value === viewModel.statuses[0])}
        isErrored={!!fieldValidationErrors.statuses}
        options={statusOptions}
        disabled={needToPickCountry || getFieldConfigByFieldId("statuses")?.disabled}
        explanation={
          needToPickCountry ? (
            <FormattedHTMLMessage id="chooseCountry" defaultMessage="Choose a country first" />
          ) : undefined
        }
        onChange={(status: FormSelectChoice) => {
          const updatedStatuses = status ? [status.value as string] : [];
          updateLicensedProfessionalViewModel("statuses", updatedStatuses);
          updateFieldValidationErrorsByFieldId(
            "statuses",
            status ? (status.value as string) : "",
            verificationService,
          );
        }}
      />
      <LicensedProfessionalOrganization
        isErrored={!!fieldValidationErrors.organization}
        onChange={(choice: Organization) => {
          updateViewModelOrganization(choice, verificationService);
          updateFieldValidationErrorsByFieldId("organization", choice, verificationService);
        }}
        value={
          (verificationService.viewModel as LicensedProfessionalPersonalInfoViewModel).organization
        }
        verificationService={verificationService}
        disabled={getFieldConfigByFieldId("organization")?.disabled}
      />
      <div className="sid-names">
        <FirstName
          value={viewModel.firstName}
          isErrored={!!fieldValidationErrors.firstName}
          disabled={getFieldConfigByFieldId("firstName")?.disabled}
          onChange={(newValue) => {
            updateLicensedProfessionalViewModel("firstName", newValue);
            updateFieldValidationErrorsByFieldId("firstName", newValue, verificationService);
          }}
        />

        <LastName
          value={viewModel.lastName}
          isErrored={!!fieldValidationErrors.lastName}
          disabled={getFieldConfigByFieldId("lastName")?.disabled}
          onChange={(newValue) => {
            updateLicensedProfessionalViewModel("lastName", newValue);
            updateFieldValidationErrorsByFieldId("lastName", newValue, verificationService);
          }}
        />
      </div>
      <BirthDate
        errorId={fieldValidationErrors.birthDate}
        locale={locale}
        onChange={(newValue) => {
          updateLicensedProfessionalViewModel("birthDate", newValue);
          updateFieldValidationErrorsByFieldId("birthDate", newValue, verificationService);
        }}
        value={viewModel.birthDate}
        disabled={getFieldConfigByFieldId("birthDate")?.disabled}
      />
      <PostalCodeComponent
        isErrored={!!fieldValidationErrors.postalCode}
        onChange={(newValue) => {
          updateLicensedProfessionalViewModel("postalCode", newValue);
          updateFieldValidationErrorsByFieldId("postalCode", newValue, verificationService);
        }}
        value={viewModel.postalCode}
        disabled={getFieldConfigByFieldId("postalCode")?.disabled}
        explanation={
          <FormattedHTMLMessage
            id="postalCodeExplanation"
            defaultMessage="Use the location on file with your licensing body"
          />
        }
      />
      <Email
        value={viewModel.email}
        isErrored={!!fieldValidationErrors.email}
        explanation={
          <FormattedHTMLMessage
            id="emailExplanation"
            defaultMessage="Needed to send you your unique code"
          />
        }
        onChange={(newValue) => {
          updateLicensedProfessionalViewModel("email", newValue);
          updateFieldValidationErrorsByFieldId("email", newValue, verificationService);
        }}
        onKeyDown={(event) => handleEmailOnKeyDown(event)}
        disabled={getFieldConfigByFieldId("email")?.disabled}
      />
      {verificationService.programTheme.isSmsNotifierConfigured ||
      verificationService.programTheme.smsLoopEnabled ? (
        <PhoneNumber
          isRequired={!!verificationService.programTheme.smsLoopEnabled}
          value={viewModel.phoneNumber}
          isErrored={!!fieldValidationErrors.phoneNumber}
          onChange={(newValue) => {
            updateLicensedProfessionalViewModel("phoneNumber", newValue);
            updateFieldValidationErrorsByFieldId("phoneNumber", newValue, verificationService);
          }}
          selectedCountryCode={viewModel.countryChoice && viewModel.countryChoice.value}
          disabled={getFieldConfigByFieldId("phoneNumber")?.disabled}
        />
      ) : null}

      {options.customFormFields.map((config: CustomFormFieldConfig) => (
        <FormFieldCustom config={config} verificationService={verificationService} />
      ))}

      <MarketConsentWrapper
        verificationService={verificationService}
        isErrored={!!fieldValidationErrors.marketConsentValue}
        onChange={(newValue) => {
          updateLicensedProfessionalViewModel("metadata", {
            ...verificationService.viewModel.metadata,
            marketConsentValue: newValue,
          });
          updateFieldValidationErrorsByFieldId("marketConsentValue", newValue, verificationService);
        }}
        viewModel={viewModel}
        disabled={getFieldConfigByFieldId("marketConsentValue")?.disabled}
      />

      <ExtraMessage verificationService={verificationService} suffix="CollectAboveSubmit" />

      <CaptchaComponent
        verificationService={verificationService}
        isErrored={!!fieldValidationErrors.captchaToken}
      />

      <PersonalInfoStepSubmitButton
        step={VerificationStepsEnum.collectLicensedProfessionalPersonalInfo}
        verificationService={verificationService}
        viewModel={viewModel}
        failedInstantMatch={failedInstantMatch}
        buttonText={
          <FormattedHTMLMessage
            id="step.personalInfo.submitButtonText"
            defaultMessage="Verify My Licensed Professional Status"
          />
        }
      />
    </PersonalInfoWrapper>
  );
};
